// i18n setup for ai.trustflow.vn — kept as a standalone client-side config
// (localStorage-persisted, no URL-based routing) since this is a React
// Router / HashRouter SPA, unlike trustflow_web's Next.js app-router
// [locale] segments. Locale codes and default ('vi') are kept IDENTICAL to
// trustflow_web's next-intl config (src/i18n.ts there) for consistency
// across the TrustFlow ecosystem, even though the two apps don't share a
// locale cookie today.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import vi from './locales/vi.json';
import en from './locales/en.json';

// Re-enabled (2026-08-20) after two independent fixes: (1) language is now
// sent as request headers (Accept-Language/language — see api.js's
// langHeaders()), never a query param, matching trustflow_web's own
// http-client and removing any chance of the `?lang=`-style query string
// being what an extension's filter rules were matching on; (2) api.js
// already captures a pristine `window.fetch` reference from index.html
// (before any extension content script can patch it) and retries once on a
// non-JSON 200 response — protection trustflow_web doesn't even have (it
// happens to dodge the issue via a hard page reload on every language
// switch, which setLocale() below also now does, for the same reason).
export const BILINGUAL_ENABLED = true;

export const LOCALES = ['vi', 'en'];
export const DEFAULT_LOCALE = 'vi';
const STORAGE_KEY = 'tf_locale';

function detectLocale() {
  if (!BILINGUAL_ENABLED) return DEFAULT_LOCALE;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LOCALES.includes(saved)) return saved;
  } catch { /* localStorage unavailable — fall through */ }
  const browser = (navigator.language || '').slice(0, 2);
  return LOCALES.includes(browser) ? browser : DEFAULT_LOCALE;
}

i18n.use(initReactI18next).init({
  resources: { vi: { translation: vi }, en: { translation: en } },
  lng: detectLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false }, // React already escapes
});

// A hard full-page reload after switching, not a client-side i18n.changeLanguage()
// — matches trustflow_web's LanguageSwitcher (window.location.href, commented
// "Use window.location for hard navigation to ensure locale context updates").
// Guarantees every in-flight request, cached component state, and the
// nativeFetch reference above all restart clean under the new language
// instead of racing a live in-memory switch.
export function setLocale(locale) {
  if (!BILINGUAL_ENABLED) return;
  if (!LOCALES.includes(locale)) return;
  try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* ignore */ }
  window.location.reload();
}

export default i18n;
